'use strict'
/**
 * validate.js — La frontera que no miente (MACRO 1 · Catarsis 1).
 *
 * Un solo punto de validación de contratos entre capas (motor↔API↔UI).
 * Política FAIL-LOUD: un contrato que no valida produce un error trazable
 * y ruidoso — NUNCA un fallback que enmascara (doctrina 15/16).
 *
 * Isomórfico: CommonJS para que lo importen backend (require) y
 * frontend (import via webpack interop).
 *
 * El reporter es inyectable: el frontend registra Sentry en src/index.js;
 * el backend usa console.error (visible en Railway logs) hasta que MACRO 2
 * añada Sentry backend (hallazgo documentado: @sentry/node ausente).
 */

let _reporter = null

/**
 * Registra el reporter de violaciones (p.ej. Sentry.captureMessage).
 * @param {(message: string, context: object) => void} fn
 */
function setContractReporter(fn) {
  _reporter = typeof fn === 'function' ? fn : null
}

/**
 * Reporta una violación de contrato: SIEMPRE console.error (Railway/devtools)
 * + reporter externo si está registrado (Sentry en frontend).
 */
function reportContractViolation({ contract, source, issues, received }) {
  const summary = (issues || [])
    .slice(0, 8)
    .map((i) => `${(i.path || []).join('.') || '(root)'}: ${i.message}`)
    .join(' · ')
  const message = `[CONTRACT VIOLATION] ${contract} @ ${source} → ${summary}`

  // Shape real recibido (truncado): la evidencia para diagnosticar productor vs esquema
  let receivedPreview = ''
  try {
    receivedPreview = JSON.stringify(received)?.slice(0, 600)
  } catch (_) {
    receivedPreview = String(received)
  }

  console.error(message, '\n  shape recibido:', receivedPreview)

  if (_reporter) {
    try {
      _reporter(message, { contract, source, issues, receivedPreview })
    } catch (_) { /* el reporter nunca debe tumbar la app */ }
  }
}

/**
 * Valida `data` contra `schema` (Zod). Fail-loud: si no valida, reporta
 * la violación con el shape real vs esperado y devuelve { ok: false }.
 *
 * @param {import('zod').ZodTypeAny} schema
 * @param {*} data
 * @param {{ contract: string, source: string }} meta — nombre del contrato y
 *   quién lo produce/consume (para el trace)
 * @returns {{ ok: true, value: * } | { ok: false, issues: object[] }}
 */
function validateContract(schema, data, meta) {
  const result = schema.safeParse(data)
  if (result.success) {
    return { ok: true, value: result.data }
  }
  reportContractViolation({
    contract: meta?.contract || 'unknown',
    source: meta?.source || 'unknown',
    issues: result.error.issues,
    received: data,
  })
  return { ok: false, issues: result.error.issues }
}

module.exports = { validateContract, setContractReporter, reportContractViolation }
